import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button, EmptyState, PageHeader, Pagination } from '@/shared/components';
import { useApi, toApiError, type CompanyResponse } from '@/shared/api';
import { AssignmentsDialog, type AssignmentSubject } from '@/features/team/AssignmentsDialog';
import { companyKeys } from '@/features/team/queries';
import { CompaniesList } from './CompaniesList';
import { CompanyFormDialog, type CompanyFormValues } from './CompanyFormDialog';

export function CompaniesPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<CompanyResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [assignSubject, setAssignSubject] = useState<AssignmentSubject | null>(null);
  const pageSize = 25;

  const { data, isLoading, error } = useQuery({
    queryKey: companyKeys.list({ page, pageSize }),
    queryFn: () => api.companies.list({ page, pageSize }),
    placeholderData: (prev) => prev,
  });

  const onError = async (err: unknown) => {
    const e = await toApiError(err);
    toast.error(e.detail ?? e.title);
  };
  const onSaved = async (label: string) => {
    await queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
    toast.success(label);
    setFormOpen(false);
    setEditing(null);
  };

  const create = useMutation({
    mutationFn: (body: CompanyFormValues) => api.companies.create(body),
    onSuccess: () => onSaved('Firma angelegt'),
    onError,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CompanyFormValues }) =>
      api.companies.update(id, body),
    onSuccess: () => onSaved('Firma gespeichert'),
    onError,
  });

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (c: CompanyResponse) => {
    setEditing(c);
    setFormOpen(true);
  };
  const submit = (values: CompanyFormValues) =>
    editing ? update.mutate({ id: editing.id, body: values }) : create.mutate(values);

  // Stabile Identität, damit der Dialog-Reset nicht bei jedem Re-Render feuert.
  const formInitial = useMemo(
    () => (editing ? { name: editing.name, slug: editing.slug, type: editing.type } : undefined),
    [editing],
  );

  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Firmen und"
        titleAccent="Kunden"
        description={
          data
            ? `${data.total} ${data.total === 1 ? 'Firma' : 'Firmen'} in diesem Workspace. Eine Firma bündelt Apartment-Zuweisungen für alle ihre Mitglieder.`
            : 'Wird geladen…'
        }
        actions={
          <Button variant="brand" leftIcon={<Plus size={14} aria-hidden />} onClick={openCreate}>
            Neue Firma
          </Button>
        }
      />

      {error ? (
        <EmptyState
          icon="Error"
          title="Die Firmen konnten nicht geladen werden"
          description="Bitte laden Sie die Seite neu. Bleibt es dabei, ist der Dienst gerade nicht erreichbar."
        />
      ) : !isLoading && data && data.items.length === 0 && page === 1 ? (
        <EmptyState
          icon="Building"
          title="Noch keine Firmen"
          description="Legen Sie die erste Firma an — Kunden oder Reinigungsfirmen, deren Mitglieder dieselben Apartments sehen sollen."
          action={{
            label: 'Neue Firma',
            leftIcon: <Plus size={14} aria-hidden />,
            onClick: openCreate,
          }}
        />
      ) : (
        <>
          <CompaniesList
            companies={data?.items ?? []}
            onEdit={openEdit}
            onAssign={(company) =>
              setAssignSubject({ kind: 'company', id: company.id, label: company.name })
            }
          />
          {data && (
            <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} />
          )}
        </>
      )}

      <CompanyFormDialog
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) setEditing(null);
        }}
        mode={editing ? 'edit' : 'create'}
        initial={formInitial}
        loading={create.isPending || update.isPending}
        onSubmit={submit}
      />
      <AssignmentsDialog
        subject={assignSubject}
        onOpenChange={(o) => !o && setAssignSubject(null)}
      />
    </>
  );
}
